import { createContext, type FC, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

/**
 * "Ask Annie about THIS" — the one channel any screen can use to open the assistant on a question it
 * already knows how to phrase.
 *
 * The problem it solves: a page that has the context (a shift, a punch, a report row) can't reach the
 * Annie panel, because the panel is mounted by `Layout` and owns its own open state. So instead of
 * lifting the whole chat, a screen hands over a QUESTION and the mounted panel picks it up, opens itself
 * and sends it.
 *
 * `isAvailable` is false unless a panel is actually mounted (Annie is flag- and role-gated, and hidden on
 * some pages), so a caller can hide its "Ask Annie" affordance rather than offer a button that does
 * nothing.
 */

/**
 * One handover. The `id` is what makes it identifiable: asking the SAME question twice is a legitimate
 * second request, so a panel that de-duplicates has to compare the ask, not its text.
 */
export interface AnnieAsk {
	id: number;
	question: string;
}

interface AnnieAskContextValue {
	panelCount: number;
	pendingAsk: AnnieAsk | null;
	ask: (question: string) => void;
	clearPendingAsk: () => void;
	registerPanel: () => () => void;
}

const AnnieAskContext = createContext<AnnieAskContextValue | null>(null);

export const AnnieAskProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [pendingAsk, setPendingAsk] = useState<AnnieAsk | null>(null);
	// A count, not a boolean: desktop and mobile mount different panel instances, and a resize can
	// briefly overlap them. Counting means the last one to unmount is the one that clears availability.
	const [panelCount, setPanelCount] = useState(0);

	const registerPanel = useCallback(() => {
		setPanelCount((count) => count + 1);
		return () => setPanelCount((count) => count - 1);
	}, []);

	const value = useMemo<AnnieAskContextValue>(
		() => ({
			panelCount,
			pendingAsk,
			// The id comes off the PREVIOUS ask, so it always advances — even for the same question text.
			ask: (question: string) => setPendingAsk((previous) => ({ id: (previous?.id ?? 0) + 1, question })),
			clearPendingAsk: () => setPendingAsk(null),
			registerPanel,
		}),
		[panelCount, pendingAsk, registerPanel],
	);

	return <AnnieAskContext.Provider value={value}>{children}</AnnieAskContext.Provider>;
};

const useAnnieAskContext = (): AnnieAskContextValue | null => useContext(AnnieAskContext);

/**
 * For any screen that wants to hand Annie a question. `isAvailable` is false when no panel is mounted
 * (or the provider isn't in the tree at all) — check it before rendering an "Ask Annie" control.
 */
export const useAnnieAsk = (): { isAvailable: boolean; ask: (question: string) => void } => {
	const context = useAnnieAskContext();
	return {
		isAvailable: (context?.panelCount ?? 0) > 0,
		// No-op rather than a throw when nothing can answer: an "Ask Annie" button is an enhancement, and
		// a page that forgets the availability check must not crash the screen it's on.
		ask: context?.ask ?? (() => undefined),
	};
};

/** For the Annie panel: register as the target, and drain whatever ask is waiting. */
export const useAnnieAskTarget = (): { pendingAsk: AnnieAsk | null; clearPendingAsk: () => void; registerPanel: () => () => void } => {
	const context = useAnnieAskContext();
	return {
		pendingAsk: context?.pendingAsk ?? null,
		clearPendingAsk: context?.clearPendingAsk ?? (() => undefined),
		registerPanel: context?.registerPanel ?? (() => () => undefined),
	};
};
